/**
 * Package Selection Modal Context
 * Shows Request Call Setup modal for signed-in users
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import RequestCallSetupModal from '../components/RequestCallSetupModal';
import logger from '../utils/logger';

const log = logger.scope('PackageSelectionModal');
const PackageSelectionModalContext = createContext();

export const usePackageSelectionModal = () => {
  const context = useContext(PackageSelectionModalContext);
  if (!context) {
    throw new Error('usePackageSelectionModal must be used within PackageSelectionModalProvider');
  }
  return context;
};

export const triggerPackageSelectionModal = (packageData, options = {}) => {
  const { isSalesPartnerRequest } = options;
  const pkg = packageData || (isSalesPartnerRequest ? { name: 'Sales Partner' } : null);
  if (!pkg) return;
  log.debug('Triggering package selection', { packageName: pkg.name, isSalesPartnerRequest });
  const event = new CustomEvent('packageSelectionModalTrigger', {
    detail: { packageData: pkg, isSalesPartnerRequest: !!isSalesPartnerRequest, timestamp: Date.now() }
  });
  window.dispatchEvent(event);
};

export const PackageSelectionModalProvider = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [packageData, setPackageData] = useState(null);
  const [isSalesPartnerRequest, setIsSalesPartnerRequest] = useState(false);

  useEffect(() => {
    const handleModalTrigger = (event) => {
      const { packageData: pkgData, isSalesPartnerRequest: salesPartner } = event.detail || {};
      if (!pkgData && !salesPartner) return;
      setPackageData(pkgData || { name: 'Sales Partner' });
      setIsSalesPartnerRequest(!!salesPartner);
      setIsModalOpen(true);
    };

    window.addEventListener('packageSelectionModalTrigger', handleModalTrigger);
    return () => window.removeEventListener('packageSelectionModalTrigger', handleModalTrigger);
  }, []);

  const hideModal = () => {
    setIsModalOpen(false);
    setPackageData(null);
    setIsSalesPartnerRequest(false);
  };

  const value = {
    showPackageSelectionModal: triggerPackageSelectionModal,
    hideModal
  };

  return (
    <PackageSelectionModalContext.Provider value={value}>
      {children}
      <RequestCallSetupModal
        isOpen={isModalOpen}
        onClose={hideModal}
        packageData={packageData}
        isSalesPartnerRequest={isSalesPartnerRequest}
      />
    </PackageSelectionModalContext.Provider>
  );
};
